import { useRef, useState } from 'react';

import { Box, Button, Input } from '@betterment/samba-react';
import { useFormikContext } from 'formik';
import * as Yup from 'yup';

import { fileToDataURLString } from '../Shared/models/filereader';
import { toYupValidationErrorString } from '../Shared/to-yup-validaiton-error';

import type { SystemStyleObject } from '@betterment/samba-styled-system/types';

interface FileUploadProps {
  name: string;
  id: string;
  maxFileSizeInMB?: number;
  css?: SystemStyleObject;
  onChange?: (file: File | null) => void;
  disabled?: boolean;
  fileNameField?: string;
}

const SUPPORTED_MIME_TYPES = ['image/jpg', 'image/jpeg', 'image/png', 'application/pdf', 'image/heic'];
const ACCEPTED_FILE_TYPES = '.jpg,.jpeg,.png,.pdf,.heic';

export const FileUpload = ({
  name,
  id,
  maxFileSizeInMB = 10,
  css,
  onChange,
  disabled,
  fileNameField,
}: FileUploadProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const { setFieldValue, setFieldError, setFieldTouched, isSubmitting } = useFormikContext();

  const fileSchema = Yup.mixed<File>()
    .test('fileSize', `File size must be less than ${maxFileSizeInMB}MB`, value => {
      if (!value) return true;
      return value.size <= maxFileSizeInMB * 1024 * 1024;
    })
    .test('fileFormat', 'Unsupported file format', value => {
      if (!value) return true;
      return SUPPORTED_MIME_TYPES.includes(value.type);
    });

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;

    if (file) {
      setFileName(file.name);

      try {
        await fileSchema.validate(file);
        const fileString = await fileToDataURLString(file);
        void setFieldValue(name, fileString);

        if (fileNameField) {
          void setFieldValue(fileNameField, file.name);
        }

        onChange?.(file);
      } catch (error) {
        void setFieldError(name, toYupValidationErrorString(error));
        void setFieldTouched(name, true, false);
        setFileName('');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } else {
      setFileName('');
      void setFieldValue(name, null);

      if (fileNameField) {
        void setFieldValue(fileNameField, null);
      }

      onChange?.(null);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box css={{ position: 'relative', ...css }}>
      <Input
        name={`${name}-display`}
        type="text"
        value={fileName}
        placeholder="No file chosen"
        readOnly={true}
        disabled={disabled || isSubmitting}
        css={{
          width: '100%',
          paddingRight: '100px',
          '&[data-readonly=true]': {
            bg: 'background.surface',
          },
        }}
      />
      <Button
        type="button"
        variant="textPrimary"
        onClick={handleBrowseClick}
        disabled={disabled || isSubmitting}
        css={{
          position: 'absolute',
          right: '8px',
          top: '50%',
          transform: 'translateY(-50%)',
        }}
      >
        Browse
      </Button>
      <input
        ref={fileInputRef}
        id={id}
        name={name}
        type="file"
        accept={ACCEPTED_FILE_TYPES}
        onChange={handleFileChange}
        data-testid={`${name}-file-input`}
        style={{ display: 'none' }}
      />
    </Box>
  );
};
